import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  HardDrive, Folder, FileText, ChevronRight, ArrowLeft, 
  Download, CloudUpload, CheckCircle2, File, Database,
  Image, FileSpreadsheet, Users, Home
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getFileIcon = (filename) => {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'tiff', 'tif'].includes(ext)) return <Image className="w-5 h-5 text-green-500" />;
  if (['pdf'].includes(ext)) return <FileText className="w-5 h-5 text-red-500" />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet className="w-5 h-5 text-emerald-600" />;
  if (['doc', 'docx'].includes(ext)) return <FileText className="w-5 h-5 text-blue-600" />;
  return <File className="w-5 h-5 text-slate-400" />;
};

const StoragePage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState('');
  const [browserData, setBrowserData] = useState({ folders: [], files: [] });
  const [browseLoading, setBrowseLoading] = useState(false);

  // Auth: cookie httpOnly (axios.defaults.withCredentials=true)

  useEffect(() => { fetchStatus(); }, []);
  useEffect(() => { fetchBrowser(currentPath); }, [currentPath]);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/storage-status`);
      setStatus(data);
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  };

  const fetchBrowser = async (prefix) => {
    setBrowseLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/storage-browse`, { params: { prefix } });
      setBrowserData(data);
    } catch (err) { console.error('Error:', err); }
    finally { setBrowseLoading(false); }
  };

  const navigateUp = () => {
    const parts = currentPath.replace(/\/$/, '').split('/');
    parts.pop();
    setCurrentPath(parts.length > 0 ? parts.join('/') + '/' : '');
  };

  const handleDownloadFile = async (filePath, filename) => {
    try {
      toast.info('Download in corso...');
      const res = await fetch(`${API_URL}/api/admin/storage-download?path=${encodeURIComponent(filePath)}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Download fallito');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download completato');
    } catch { toast.error('Errore download'); }
  };

  const breadcrumbs = currentPath ? currentPath.replace(/\/$/, '').split('/') : [];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-700" /></div>;
  }

  const fm = status?.fattura_manuale || {};
  const dc = status?.documenti_contratto || {};
  const du = status?.documenti_utenti || {};
  const di = status?.documenti_immobili || {};

  return (
    <div data-testid="storage-page">
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="storage-title">Gestione Storage</h1>
            <p className="text-slate-500 text-sm">Aruba Cloud Object Storage — File manager</p>
          </div>
          <Badge variant="outline" className="text-sm">
            Totale: {formatBytes(status?.totale_size_bytes)}
          </Badge>
        </div>
      </div>

      <div className="page-content space-y-6">
        {/* Stats cards con dimensioni */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CloudUpload className="w-4 h-4 text-blue-500" />
                <p className="text-xs text-slate-500 uppercase tracking-wide">Fatture</p>
              </div>
              <p className="text-2xl font-bold">{fm.totale || 0}</p>
              <p className="text-xs text-slate-400 mt-0.5">{formatBytes(fm.size_bytes)}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-indigo-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-indigo-500" />
                <p className="text-xs text-slate-500 uppercase tracking-wide">Doc. Contratto</p>
              </div>
              <p className="text-2xl font-bold">{dc.totale || 0}</p>
              <p className="text-xs text-slate-400 mt-0.5">{formatBytes(dc.size_bytes)}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-teal-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-teal-500" />
                <p className="text-xs text-slate-500 uppercase tracking-wide">Doc. Utenti</p>
              </div>
              <p className="text-2xl font-bold">{du.totale || 0}</p>
              <p className="text-xs text-slate-400 mt-0.5">{formatBytes(du.size_bytes)}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Home className="w-4 h-4 text-amber-500" />
                <p className="text-xs text-slate-500 uppercase tracking-wide">Doc. Immobili</p>
              </div>
              <p className="text-2xl font-bold">{di.totale || 0}</p>
              <p className="text-xs text-slate-400 mt-0.5">{formatBytes(di.size_bytes)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Migration status */}
        {status && (
          <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm ${status.migrazione_completa ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
            {status.migrazione_completa ? <CheckCircle2 className="w-4 h-4" /> : <Database className="w-4 h-4" />}
            {status.migrazione_completa ? 'Tutti i file su Aruba S3' : 'Migrazione in corso'}
          </div>
        )}

        {/* File Browser */}
        <Card>
          <CardHeader className="pb-2 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <HardDrive className="w-5 h-5 text-slate-600" />
                Esplora File
              </CardTitle>
              <Badge variant="outline" className="text-xs">
                {browserData.folders.length} cartelle, {browserData.files.length} file
              </Badge>
            </div>
            {/* Breadcrumb */}
            <div className="flex items-center gap-1 text-sm mt-2 flex-wrap">
              <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-blue-600 hover:text-blue-800"
                onClick={() => setCurrentPath('')} data-testid="breadcrumb-root">
                <HardDrive className="w-3.5 h-3.5 mr-1" /> Root
              </Button>
              {breadcrumbs.map((crumb, i) => (
                <React.Fragment key={`${crumb}-${i}`}>
                  <ChevronRight className="w-3 h-3 text-slate-400" />
                  <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-blue-600 hover:text-blue-800"
                    onClick={() => setCurrentPath(breadcrumbs.slice(0, i + 1).join('/') + '/')}>
                    {crumb.length > 30 ? crumb.substring(0, 12) + '...' : crumb}
                  </Button>
                </React.Fragment>
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {browseLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-700" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead className="w-28 text-right">Dimensione</TableHead>
                    <TableHead className="w-36">Ultima Modifica</TableHead>
                    <TableHead className="w-20 text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentPath && (
                    <TableRow className="cursor-pointer hover:bg-slate-50" onClick={navigateUp} data-testid="navigate-up">
                      <TableCell><ArrowLeft className="w-5 h-5 text-slate-400" /></TableCell>
                      <TableCell className="font-medium text-slate-600">..</TableCell>
                      <TableCell /><TableCell /><TableCell />
                    </TableRow>
                  )}
                  {browserData.folders.map((folder) => (
                    <TableRow key={folder.path} className="cursor-pointer hover:bg-blue-50/50 transition-colors"
                      onClick={() => setCurrentPath(folder.path)} data-testid={`folder-${folder.name}`}>
                      <TableCell><Folder className="w-5 h-5 text-amber-500" /></TableCell>
                      <TableCell>
                        <span className="font-medium">{folder.display_name || folder.name}</span>
                        {folder.display_name && folder.display_name !== folder.name && (
                          <span className="text-xs text-slate-400 ml-2 hidden lg:inline">({folder.name.substring(0, 8)}...)</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-400">—</TableCell>
                      <TableCell className="text-sm text-slate-400">—</TableCell>
                      <TableCell className="text-right"><ChevronRight className="w-4 h-4 text-slate-400 ml-auto" /></TableCell>
                    </TableRow>
                  ))}
                  {browserData.files.map((file) => (
                    <TableRow key={file.path} className="hover:bg-slate-50/50" data-testid={`file-${file.name}`}>
                      <TableCell>{getFileIcon(file.display_name || file.name)}</TableCell>
                      <TableCell>
                        <span className="text-sm font-medium">{file.display_name || file.name}</span>
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-500">{formatBytes(file.size)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {file.last_modified ? new Date(file.last_modified).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDownloadFile(file.path, file.display_name || file.name); }}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {browserData.folders.length === 0 && browserData.files.length === 0 && !currentPath && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-400">Storage vuoto</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StoragePage;
